import { useEffect, useState } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAccess } from '../access.js';
import { api, retryRead, readRetryDelay } from '../api.js';
import { Priority, Website, STATUS_LABELS, observedAt } from '../components/LeadFacts.jsx';

export default function Leads() {
  const { can } = useAccess();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const filters = Object.fromEntries(params);
  const [form, setForm] = useState(filters);
  const [selected, setSelected] = useState([]);
  useEffect(() => {
    setForm(Object.fromEntries(params));
    setSelected([]);
  }, [params]);
  const leadsQ = useQuery({
    queryKey: ['leads', filters],
    queryFn: () => api.listLeads(filters),
    retry: retryRead,
    retryDelay: readRetryDelay,
  });
  const rows = leadsQ.data?.items ?? [];
  const { offset: _offset, ...exportFilters } = filters;
  const exportM = useMutation({
    mutationFn: (input) => api.createExport(input),
    onSuccess: (row) => {
      window.location.href = `/api/exports/${row.id}`;
    },
  });
  const update = (name, value) => setForm((current) => ({ ...current, [name]: value }));
  const apply = (next) => {
    setParams(
      Object.fromEntries(Object.entries(next).filter(([, v]) => v !== '' && v !== undefined)),
    );
    setSelected([]);
  };
  const exportRows = (ids) =>
    exportM.mutate({
      requestId: crypto.randomUUID(),
      filters: { ...exportFilters, ...(ids ? { ids } : {}) },
    });
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 justify-between items-start">
        <div>
          <h1 className="text-2xl font-semibold">Base de leads</h1>
          <p className="text-sm text-slate-500 mt-1">
            Consulte e trabalhe as empresas já encontradas, sem uma nova busca paga.
          </p>
        </div>
        {can('campaigns.write') && (
          <Link className="btn-primary" to="/campaigns/new">
            Nova campanha
          </Link>
        )}
      </div>
      <form
        className="bg-white border border-slate-200 rounded-xl p-5 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          apply({ ...form, offset: '' });
        }}
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <label className="text-sm space-y-1 block">
            <span>Origem dos dados</span>
            <select
              className="input"
              value={form.dataKind ?? 'production'}
              onChange={(e) => update('dataKind', e.target.value)}
            >
              <option value="production">Produção</option>
              <option value="demo">Demonstração (legado)</option>
              <option value="all">Todos, incluindo demonstração</option>
            </select>
          </label>
          {[
            ['q', 'Empresa', 'Nome da empresa'],
            ['region', 'Região', 'Cidade, bairro ou endereço'],
            ['segment', 'Segmento', 'Ex.: supermercado'],
          ].map(([name, label, placeholder]) => (
            <label key={name} className="text-sm space-y-1 block">
              <span>{label}</span>
              <input
                className="input"
                value={form[name] ?? ''}
                placeholder={placeholder}
                onChange={(e) => update(name, e.target.value)}
              />
            </label>
          ))}
          <label className="text-sm space-y-1 block">
            <span>Status</span>
            <select
              className="input"
              value={form.status ?? ''}
              onChange={(e) => update('status', e.target.value)}
            >
              <option value="">Todos</option>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm space-y-1 block">
            <span>Site</span>
            <select
              className="input"
              value={form.website ?? 'all'}
              onChange={(e) => update('website', e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="found">Site encontrado</option>
              <option value="missing">Site não encontrado</option>
            </select>
          </label>
          <label className="text-sm space-y-1 block">
            <span>Contato disponível</span>
            <select
              className="input"
              value={form.contact ?? 'all'}
              onChange={(e) => update('contact', e.target.value)}
            >
              {[
                ['all', 'Todos'],
                ['any', 'Com algum contato'],
                ['phone', 'Com telefone'],
                ['email', 'Com e-mail'],
                ['whatsapp', 'WhatsApp publicado na fonte'],
                ['none', 'Sem contato registrado'],
              ].map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm space-y-1 block">
            <span>Aderência mínima (0 a 100)</span>
            <input
              type="number"
              min="0"
              max="100"
              className="input"
              value={form.minScore ?? ''}
              onChange={(e) => update('minScore', e.target.value)}
            />
          </label>
          <label className="text-sm space-y-1 block">
            <span>Ordenação</span>
            <select
              className="input"
              value={form.sort ?? 'priority'}
              onChange={(e) => update('sort', e.target.value)}
            >
              <option value="priority">Prioridade e confiança</option>
              <option value="recent">Consulta mais recente</option>
              <option value="name">Nome da empresa</option>
            </select>
          </label>
        </div>
        <div className="flex items-center flex-wrap gap-3">
          <button className="btn-primary" type="submit">
            Aplicar filtros
          </button>
          <button
            className="btn-secondary"
            type="button"
            onClick={() => {
              setForm({});
              apply({});
            }}
          >
            Limpar filtros
          </button>
          {filters.campaignId && (
            <span className="text-sm text-slate-500">Resultados da campanha selecionada</span>
          )}
        </div>
      </form>
      <div className="flex items-center justify-between flex-wrap gap-3 text-sm">
        <span className="text-slate-500">
          {selected.length
            ? `${selected.length} ${selected.length === 1 ? 'selecionada' : 'selecionadas'}`
            : `${rows.length} empresas nesta página`}
        </span>
        {can('leads.export') && (
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-secondary"
              disabled={!selected.length || exportM.isPending}
              onClick={() => exportRows(selected)}
            >
              Exportar selecionadas
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={exportM.isPending || leadsQ.isLoading || !rows.length}
              onClick={() => exportRows()}
            >
              {exportM.isPending ? 'Exportando…' : 'Exportar resultado dos filtros'}
            </button>
          </div>
        )}
      </div>
      {(leadsQ.error || exportM.error) && (
        <p role="alert" className="text-red-600">
          {(leadsQ.error || exportM.error).message}
        </p>
      )}
      {leadsQ.error && (
        <button className="btn-secondary" onClick={() => leadsQ.refetch()}>
          Tentar carregar a base novamente
        </button>
      )}
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        {leadsQ.isLoading ? (
          <p className="p-6 text-slate-500">Carregando empresas…</p>
        ) : rows.length ? (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr>
                <th className="p-3">
                  <input
                    type="checkbox"
                    aria-label="Selecionar todas desta página"
                    checked={rows.length > 0 && selected.length === rows.length}
                    onChange={(e) => setSelected(e.target.checked ? rows.map((r) => r.id) : [])}
                  />
                </th>
                {[
                  'Empresa e região',
                  'Site e contatos',
                  'Prioridade',
                  'Fonte e consulta',
                  'Status',
                ].map((text) => (
                  <th className="text-left p-3" key={text}>
                    {text}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-slate-100 align-top">
                  <td className="p-3">
                    <input
                      type="checkbox"
                      aria-label={`Selecionar ${row.canonical_name}`}
                      checked={selected.includes(row.id)}
                      onChange={(e) =>
                        setSelected((current) =>
                          e.target.checked
                            ? [...current, row.id]
                            : current.filter((id) => id !== row.id),
                        )
                      }
                    />
                  </td>
                  <td className="p-3 min-w-48">
                    <Link
                      to={`/leads/${row.id}`}
                      state={{ from: `${location.pathname}${location.search}` }}
                      className="font-medium text-brand-700 hover:underline"
                    >
                      {row.canonical_name}
                    </Link>
                    {row.isDemo && (
                      <p className="text-xs text-amber-800 mt-1">
                        Demonstração — não usar para prospecção
                      </p>
                    )}
                    <p className="text-xs text-slate-500 mt-1">
                      {row.categoryLabel ||
                        row.category ||
                        row.segment ||
                        'Segmento não registrado'}
                    </p>
                    <Link
                      to={`/leads/${row.id}`}
                      state={{ from: `${location.pathname}${location.search}` }}
                      className="inline-block mt-2 text-xs text-brand-700 hover:underline"
                    >
                      Ver detalhes →
                    </Link>
                    <p className="text-xs text-slate-500 mt-1">
                      {row.address || row.region || 'Região não registrada'}
                    </p>
                  </td>
                  <td className="p-3 min-w-48 space-y-1">
                    <Website lead={row} />
                    <p>
                      {row.phoneHref ? (
                        <a className="text-brand-700 hover:underline" href={row.phoneHref}>
                          {row.phone}
                        </a>
                      ) : (
                        row.phone || 'Telefone não encontrado'
                      )}
                    </p>
                    {row.email && (
                      <p>
                        {row.emailHref ? (
                          <a className="text-brand-700 hover:underline" href={row.emailHref}>
                            {row.email}
                          </a>
                        ) : (
                          row.email
                        )}
                      </p>
                    )}
                    <p className="text-xs text-slate-500">
                      {row.whatsapp
                        ? `WhatsApp publicado: ${row.whatsapp.value}`
                        : 'WhatsApp não verificado'}
                    </p>
                  </td>
                  <td className="p-3 min-w-48">
                    <Priority lead={row} />
                    <p className="text-xs text-slate-500 mt-2">{row.priorityReason}</p>
                    {row.confidence !== null && (
                      <p className="text-xs text-slate-500 mt-1">
                        Confiança: {Math.round(row.confidence * 100)}%
                      </p>
                    )}
                  </td>
                  <td className="p-3 min-w-40">
                    <p>{row.provider || 'Fonte não registrada'}</p>
                    <p className="text-xs text-slate-500 mt-1">{observedAt(row.observed_at)}</p>
                    {row.sourceUrl && (
                      <a
                        className="text-brand-700 text-xs hover:underline"
                        target="_blank"
                        rel="noreferrer"
                        href={row.sourceUrl}
                      >
                        Ver fonte
                      </a>
                    )}
                  </td>
                  <td className="p-3">{STATUS_LABELS[row.status] ?? row.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          !leadsQ.error && (
            <p className="p-8 text-slate-500">Nenhuma empresa encontrada com estes filtros.</p>
          )
        )}
      </div>
      <div className="flex justify-end items-center gap-3 text-sm">
        <button
          className="btn-secondary"
          disabled={!Number(filters.offset)}
          onClick={() =>
            apply({ ...filters, offset: String(Math.max(0, Number(filters.offset) - 100)) })
          }
        >
          Anterior
        </button>
        <span>Página {Math.floor(Number(filters.offset || 0) / 100) + 1}</span>
        <button
          className="btn-secondary"
          disabled={leadsQ.data?.nextOffset == null || leadsQ.isFetching}
          onClick={() => apply({ ...filters, offset: String(leadsQ.data.nextOffset) })}
        >
          Próxima
        </button>
      </div>
    </div>
  );
}
